import { useEffect } from "react";

/**
 * Track the one thing CSS cannot know on its own: how much of the layout
 * viewport the software keyboard currently covers.
 *
 * `--kb` is that covered height: `innerHeight - visualViewport.height -
 * visualViewport.offsetTop`. `--vv-top` is `visualViewport.offsetTop`, for
 * anything that needs to correct for the visual viewport scrolling under the
 * layout viewport when the keyboard opens.
 *
 * Measured on an iPhone (iOS 18, Safari 27, tab mode, 2026-08-25): `dvh`,
 * `svh`, `visualViewport.height` and `innerHeight` all agreed (796px). The
 * earlier claim that iOS 26's floating toolbar broke those APIs is not
 * supported by the numbers. The app was short of the screen because the
 * document was `position: fixed; overflow: hidden` — Safari only retracts
 * its toolbar in response to document scrolling, so a locked document is
 * permanently confined to the toolbar-expanded viewport.
 *
 * Dashboard routes therefore scroll the document. Chat is the exception:
 * a pinned composer needs an internally-scrolling thread, so that route
 * locks the shell to `100svh` and uses `--kb` to translate the composer
 * against the visual viewport (see `.shell[data-locked]` in base.css).
 */
export function useViewport(): void {
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;

    const apply = () => {
      if (!vv) {
        root.style.setProperty("--kb", "0px");
        root.style.setProperty("--vv-top", "0px");
        root.classList.remove("kb-open");
        return;
      }
      // layout height - visual height - how far the visual viewport is scrolled
      const covered = Math.max(
        0,
        Math.round(window.innerHeight - vv.height - vv.offsetTop),
      );
      root.style.setProperty("--kb", `${covered}px`);
      root.style.setProperty("--vv-top", `${Math.round(vv.offsetTop)}px`);
      root.classList.toggle("kb-open", covered > 80);
    };

    apply();
    // A couple of delayed re-reads: iOS can report a transient value in the
    // first frame or two after a keyboard show/hide before it settles.
    const settle = [100, 500].map((ms) => window.setTimeout(apply, ms));

    if (vv) {
      vv.addEventListener("resize", apply);
      vv.addEventListener("scroll", apply);
    }
    window.addEventListener("orientationchange", apply);
    window.addEventListener("resize", apply);
    window.addEventListener("pageshow", apply);

    return () => {
      settle.forEach((id) => window.clearTimeout(id));
      if (vv) {
        vv.removeEventListener("resize", apply);
        vv.removeEventListener("scroll", apply);
      }
      window.removeEventListener("orientationchange", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("pageshow", apply);
    };
  }, []);
}
